<template>
  <section class="config-group style" aria-label="General">
    <div class="row">
      <div class="column">
        <h3>{{texts.general.style}}</h3>
        <div class="row theme-color">
          <ExpandableColorPicker
            type="inline"
            :title="texts.general.themeColor"
            :value="color"
            :onChange="updateColor"
          />
        </div>        
		<div class="row theme-color">
        <ExpandableColorPicker
            type="inline"
            :title="texts.general.accentColor"
            :value="accentColor"
            :onChange="updateAccentColor"
          />
        </div>
        <div class="row stretch loader">
          <label>{{texts.general.loadingAnimation}}</label>
          <div>
            <select :value="loader" @change="updateLoader">
              <option value="skeleton">{{texts.general.skeleton}}</option>
              <option value="circle">{{texts.general.circle}}</option>
              <option value="square">{{texts.general.square}}</option>
            </select>
            <LoaderPreview :type="loader" :color="accentColor" :speed="parseInt(animationSpeed)"/>
          </div>
        </div>
        <div class="row stretch">
          <label>{{texts.general.animationSpeed}}</label>
          <select :value="animationSpeed" @change="updateAnimationSpeed">
            <option value="50">{{texts.general.speedFast}}</option>
            <option value="250">{{texts.general.speedMedium}}</option>
            <option value="500">{{texts.general.speedSlow}}</option>
          </select>
        </div>
		<div class="row stretch">
			<label>{{texts.general.interfaceLanguage}}</label>
			<select v-model="config.language" @change="updateConfigStrings">
				<option v-for="lang in interfaceLanguages" :key="lang.tag" :value="lang.tag">{{lang.name}}</option>
			</select>
		</div>
		<div class="row stretch">
			<label>{{texts.general.additionalCss}}</label>
			<PrismEditor :code="additionalCss" language="css" :lineNumbers="true" @change="updateAdditionalCss"></PrismEditor>
		</div>
      </div>
      <div class="column">
        <h3>{{texts.general.tracking}}</h3>
        <Checkmark
          :label="texts.general.ga"
          :value="ga"
          @input="onGaChange"
          checkbox-id="ga-tracking--i"
        />
        <Checkmark
          :label="texts.general.gtm"
          :value="gtm"
          @input="onGtmChange"
          checkbox-id="gtm-tracking--i"
        />
        <Checkmark
          :label="texts.general.allowCookies"
          v-model="config.allowCookies"
          checkbox-id="cookies--i"
        />
      </div>
    </div>
  </section>
</template>

<script>
import Vue from "vue";
import ExpandableColorPicker from "../ExpandableColorPicker";
import LoaderPreview from "./LoaderPreview";
import Checkmark from "../Checkmark";
import PrismEditor from 'vue-prism-editor';
import 'prismjs';
import "prismjs/themes/prism.css";
import CleanCss from 'clean-css';
import cssbeautify from 'cssbeautify';
import Languages from '../../assets/SupportedLanguages';
import ConfigHelper from '../../utils/ConfigHelper';
import LanguageStrings from '../../assets/LanguageStrings';

export default {
  name: "General",
  components: { ExpandableColorPicker, LoaderPreview, Checkmark, PrismEditor },
  props: {
    config: {
      type: Object,
      required: true
    }
  },
  data() {
	  return {
		  setStyleTimeout: -1
	  }
  },
  computed: {
    color() {
      return this.config.style && this.config.style.themeColor
        ? this.config.style.themeColor
        : "#1C5D7D";
	},
	accentColor() {
		return this.config.style && this.config.style.accentColor ? this.config.style.accentColor : '#3d8fff';
	},
    loader() {
      return this.config.style && this.config.style.loaderType
        ? this.config.style.loaderType
        : "circle";
    },
    animationSpeed() {
      return this.config.style && this.config.style.animationSpeed
        ? this.config.style.animationSpeed.toString()
        : "250";
    },
    ga() {
      return this.config.tracking.providers.indexOf("GA") !== -1;
    },
    gtm() {
      return this.config.tracking.providers.indexOf("GTM") !== -1;
	},
	interfaceLanguages() {
		const names = this.texts !== undefined ? {
			en: this.texts.searchBox.en,
			de: this.texts.searchBox.languages.de,
			nl: this.texts.searchBox.languages.nl,
			fr: this.texts.searchBox.languages['fr-FR']
		} : {
			en: 'English',
			de: 'German',
			nl: 'Dutch',
			fr: 'French'
		};
		return [
				{tag: 'en', name: names.en},
				{tag: 'de', name: names.de},
				{tag: 'nl', name: names.nl},
				{tag: 'fr', name: names.fr},
		]
	},
	additionalCss() {
		const css = (this.config.style || {}).additionalCss || '';
		return cssbeautify(css);
	},
  },
  methods: {
	updateConfigStrings() {
		let lang = undefined;
		const language = this.config.language;
		if (language === 'de') {
			lang = LanguageStrings.DE;
		} else if(language === 'fr') {
			lang = LanguageStrings.FR;
		} else if(language === 'nl') {
			lang = LanguageStrings.NL;
		} else {
			lang = LanguageStrings.EN;
		}
		Object.keys(lang).forEach((key) => {
			const target = this.config[key];
			ConfigHelper.extend(target, lang[key]);
		})
	},
    updateColor(color) {
      if (this.config.style === undefined) {
        Vue.set(this.config, "style", {});
      }
      Vue.set(this.config.style, "themeColor", color.hex);
	},  
	updateAccentColor(color) {
      if (this.config.style === undefined) {
        Vue.set(this.config, "style", {});
      }
	  Vue.set(this.config.style, "accentColor", color.hex);
	  if (this.config.style.searchBox !== undefined && this.config.style.searchBox.button !== undefined) {
		  this.config.style.searchBox.button.backgroundColor = color.hex;
	  }
    },
    updateLoader(e) {
      if (this.config.style === undefined) {
        Vue.set(this.config, "style", {});
      }
      Vue.set(this.config.style, "loaderType", e.target.value);
    },
    updateAnimationSpeed(e) {
      if (this.config.style === undefined) {
        Vue.set(this.config, "style", {});
      }
      Vue.set(this.config.style, "animationSpeed", parseInt(e.target.value));
    },
    onGaChange(e) {
      if (e && this.config.tracking.providers.indexOf("GA") === -1) {
        this.config.tracking.providers.push("GA");
      } else if (!e) {
        this.config.tracking.providers.splice(this.config.tracking.providers.indexOf("GA"), 1);
      }
    },
    onGtmChange(e) {
      if (e && this.config.tracking.providers.indexOf("GTM") === -1) {
        this.config.tracking.providers.push("GTM");
      } else if (!e) {
        this.config.tracking.providers.splice(this.config.tracking.providers.indexOf("GTM"), 1);
      }
	},
	updateAdditionalCss(e) {
		clearTimeout(this.setStyleTimeout);
		this.setStyleTimeout = setTimeout(() => {
			const output = new CleanCss().minify(e);
			Vue.set(this.config.style, 'additionalCss', output.styles)
		}, 2000);
	}
  }
};
</script>

<style>
.config-group.style select {
  width: 100%;
  max-width: 225px;
  margin: 0;
  min-width: 150px;
  padding-left: 16px;
}

.config-group.style .row {
  margin: 0.5em 0;
}

#app .config-group.style .loader {
  align-items: flex-start;
}

.config-group.style div.loader > div {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.prism-editor-wrapper {
	align-items: flex-start;
	display: flex;
	max-width: 225px;
	min-width: 225px;
	min-height: 100px;
}

.prism-editor-wrapper pre {
	width: 100%;
}

.prism-editor-wrapper pre code {
	font-family: monospace;
	font-size: 1em;
}
</style>



// WEBPACK FOOTER //
// src/components/configuration/General.vue